import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtMonto = (monto) => (monto ?? 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

const ESTADOS = ['Pendiente', 'Cancelada', 'Finalizada'];

const ESTADO_OPTIONS = [
    { label: 'Todos', value: '' },
    ...ESTADOS.map(e => ({ label: e, value: e })),
];

const ESTADO_COLORS = {
    'Pendiente':  { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
    'Cancelada':  { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' },
    'Finalizada': { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
};

const estadoBadge = (estado) => {
    const s = ESTADO_COLORS[estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99 }}>
            {estado}
        </span>
    );
};

const initialForm = { infraccionId: 0, unidadFuncionalId: null, personaId: null, fecha: new Date(), motivo: '', monto: null, estado: 'Pendiente' };

const InfraccionesAdmin = () => {
    const toast      = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();
    const t = useTerminologia();

    const [infracciones,  setInfracciones]  = useState([]);
    const [unidades,      setUnidades]      = useState([]);
    const [personas,      setPersonas]      = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [filtroEstado,  setFiltroEstado]  = useState('');

    const [visible,  setVisible]  = useState(false);
    const [editando, setEditando] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [saving,   setSaving]   = useState(false);

    const hc = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const fetchInfracciones = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/infracciones/GetInfracciones?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setInfracciones(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las infracciones', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    const fetchUnidades = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`);
            setUnidades(data);
        } catch { /* silencioso */ }
    }, [barrio?.id, administracionId]);

    const fetchPersonas = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(`${HOST_API}api/personas/GetPersonas?administracionId=${administracionId}&personaId=0&barrioId=${barrio.id}`);
            setPersonas(data);
        } catch { /* silencioso */ }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchInfracciones(); fetchUnidades(); fetchPersonas(); }, [fetchInfracciones, fetchUnidades, fetchPersonas]);

    const abrirNueva = () => {
        setFormData(initialForm);
        setEditando(false);
        setVisible(true);
    };

    const abrirEdicion = (i) => {
        setFormData({
            infraccionId: i.infraccionId,
            unidadFuncionalId: i.unidadFuncionalId,
            personaId: i.personaId ?? null,
            fecha: i.fecha ? new Date(i.fecha) : new Date(),
            motivo: i.motivo || '',
            monto: i.monto,
            estado: i.estado,
        });
        setEditando(true);
        setVisible(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.unidadFuncionalId) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: `Seleccioná la ${t.UF}`, life: 3000 });
            return;
        }
        if (!formData.fecha) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná la fecha', life: 3000 });
            return;
        }
        if (!formData.motivo?.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingresá el motivo', life: 3000 });
            return;
        }
        if (!formData.monto) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingresá el monto', life: 3000 });
            return;
        }

        const d = formData.fecha;
        const fechaStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        setSaving(true);
        try {
            if (editando) {
                await axios.put(`${HOST_API}api/infracciones/UpdateInfraccion`, {
                    infraccionId: formData.infraccionId,
                    administracionId,
                    barrioId: barrio.id,
                    unidadFuncionalId: formData.unidadFuncionalId,
                    personaId: formData.personaId || null,
                    fecha: fechaStr,
                    motivo: formData.motivo,
                    monto: formData.monto,
                });
                if (formData.estado) {
                    await axios.put(`${HOST_API}api/infracciones/CambiarEstado`, {
                        infraccionId: formData.infraccionId,
                        administracionId,
                        estado: formData.estado,
                    });
                }
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Infracción actualizada', life: 2500 });
            } else {
                await axios.post(`${HOST_API}api/infracciones/AddInfraccion`, {
                    administracionId,
                    barrioId: barrio.id,
                    unidadFuncionalId: formData.unidadFuncionalId,
                    personaId: formData.personaId || null,
                    fecha: fechaStr,
                    motivo: formData.motivo,
                    monto: formData.monto,
                    usuarioId,
                });
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Infracción registrada y notificada', life: 3000 });
            }
            setVisible(false);
            fetchInfracciones();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar la infracción', life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    const handleEliminar = (i) => {
        confirmDialog({
            message: `¿Eliminar la infracción de ${i.unidadFuncional}?`,
            header: 'Eliminar infracción',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, eliminar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/infracciones/DeleteInfraccion?infraccionId=${i.infraccionId}&administracionId=${administracionId}`);
                    fetchInfracciones();
                    toast.current.show({ severity: 'info', summary: 'Eliminada', detail: 'Infracción eliminada', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la infracción', life: 3000 });
                }
            },
        });
    };

    const infraccionesFiltradas = infracciones.filter(i => !filtroEstado || i.estado === filtroEstado);
    const pendientesCount = infracciones.filter(i => i.estado === 'Pendiente').length;

    const estadoTemplate  = (i) => estadoBadge(i.estado);
    const fechaTemplate   = (i) => fmtFecha(i.fecha);
    const montoTemplate   = (i) => fmtMonto(i.monto);
    const motivoTemplate  = (i) => i.motivo
        ? <span title={i.motivo} style={{ color: '#344054' }}>{i.motivo.length > 60 ? i.motivo.substring(0, 60) + '…' : i.motivo}</span>
        : <span style={{ color: '#d0d5dd' }}>—</span>;
    const personaTemplate = (i) => i.nombrePersona || <span style={{ color: '#d0d5dd' }}>—</span>;

    const accionesTemplate = (i) => (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
            <Button icon="pi pi-pencil" rounded text severity="warning" tooltip="Editar" tooltipOptions={{ position: 'left' }} onClick={() => abrirEdicion(i)} />
            <Button icon="pi pi-trash" rounded text severity="danger" tooltip="Eliminar" tooltipOptions={{ position: 'left' }} onClick={() => handleEliminar(i)} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div>
                        <h4 style={{ margin: 0, color: '#101828' }}>Infracciones</h4>
                        <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>Gestión de infracciones del barrio</p>
                    </div>
                    {pendientesCount > 0 && (
                        <span style={{ background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca', fontWeight: 700, fontSize: '0.8rem', padding: '0.2rem 0.65rem', borderRadius: 99 }}>
                            {pendientesCount} pendiente{pendientesCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <Button label="Nueva infracción" icon="pi pi-plus" onClick={abrirNueva} disabled={!barrio?.id} />
            </div>

            {!barrio?.id
                ? <p style={{ color: '#98a2b3' }}>Seleccioná un barrio para ver las infracciones.</p>
                : (
                    <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', marginBottom: '1rem' }}>
                            <Dropdown
                                value={filtroEstado}
                                options={ESTADO_OPTIONS}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => setFiltroEstado(e.value)}
                                className="p-inputtext-sm"
                                style={{ minWidth: 160 }}
                            />
                            {filtroEstado && (
                                <Button icon="pi pi-filter-slash" text severity="secondary" tooltip="Limpiar filtro" onClick={() => setFiltroEstado('')} />
                            )}
                        </div>

                        <DataTable
                            value={infraccionesFiltradas}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[10, 15, 30, 50]}
                            emptyMessage="No hay infracciones para mostrar"
                            sortField="fecha"
                            sortOrder={-1}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="unidadFuncional" header={t.UF}      sortable style={{ minWidth: 130 }} />
                            <Column header="Persona"         body={personaTemplate} style={{ minWidth: 150 }} />
                            <Column field="fecha"            header="Fecha"    sortable body={fechaTemplate} style={{ minWidth: 100 }} />
                            <Column header="Motivo"          body={motivoTemplate} style={{ minWidth: 200 }} />
                            <Column field="monto"            header="Monto"    sortable body={montoTemplate} style={{ minWidth: 120 }} />
                            <Column field="estado"           header="Estado"   sortable body={estadoTemplate} style={{ minWidth: 130 }} />
                            <Column header=""                body={accionesTemplate} style={{ width: 100, textAlign: 'center' }} />
                        </DataTable>
                    </>
                )
            }

            <Dialog header={editando ? 'Editar infracción' : 'Nueva infracción'} visible={visible} style={{ width: 460 }} breakpoints={{ '960px': '100vw' }} modal onHide={() => setVisible(false)}>
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={labelStyle}>{t.UF} <span style={req}>*</span></label>
                            <Dropdown
                                value={formData.unidadFuncionalId}
                                options={unidades}
                                optionLabel="unidadFuncional"
                                optionValue="unidadFuncionalId"
                                onChange={(e) => hc('unidadFuncionalId', e.value)}
                                placeholder={`Seleccioná la ${t.UF}`}
                                className="p-inputtext-sm w-full"
                                filter
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Persona <span style={{ color: '#98a2b3', fontWeight: 400 }}>(opcional)</span></label>
                            <Dropdown
                                value={formData.personaId}
                                options={personas}
                                optionLabel="nombreCompleto"
                                optionValue="personaId"
                                onChange={(e) => hc('personaId', e.value)}
                                placeholder="Sin especificar"
                                className="p-inputtext-sm w-full"
                                filter
                                showClear
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Fecha <span style={req}>*</span></label>
                            <Calendar
                                value={formData.fecha}
                                onChange={(e) => hc('fecha', e.value)}
                                dateFormat="dd/mm/yy"
                                maxDate={new Date()}
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Motivo <span style={req}>*</span></label>
                            <InputTextarea
                                value={formData.motivo}
                                onChange={(e) => hc('motivo', e.target.value)}
                                rows={3}
                                autoResize
                                className="w-full"
                                placeholder="Describí el motivo de la infracción…"
                                maxLength={500}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Monto <span style={req}>*</span></label>
                            <InputNumber
                                value={formData.monto}
                                onValueChange={(e) => hc('monto', e.value)}
                                mode="currency" currency="ARS" locale="es-AR"
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        {editando && (
                            <div>
                                <label style={labelStyle}>Estado</label>
                                <Dropdown
                                    value={formData.estado}
                                    options={ESTADOS.map(e => ({ label: e, value: e }))}
                                    onChange={(e) => hc('estado', e.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            </div>
                        )}

                        <div className="flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label={saving ? 'Guardando…' : 'Guardar'} icon={saving ? undefined : 'pi pi-save'} type="submit" disabled={saving} />
                        </div>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const req        = { color: '#f04438' };

export default InfraccionesAdmin;
